/**
 * CreditsBarVariant — Cart Summary V2
 *
 * Stacks three sections above the DefaultVariant:
 *   1. Free shipping progress bar (C1)
 *   2. Credits progress bar (C5) — hidden when user is logged out or disabled
 *
 * Resolves the current user ID via a light React Query fetch so the hook can
 * retrieve the credit balance without prop-drilling userId into SideCart.
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { authService } from '@/lib/services/auth.service';
import { useFreeShipping } from '@/lib/hooks/use-free-shipping';
import { useCreditsProgress } from '@/lib/hooks/use-credits';
import { FreeShippingProgressBar } from '../FreeShippingProgressBar';
import { CreditsProgressBar } from '../CreditsProgressBar';
import { DefaultVariant } from './DefaultVariant';
import type { VariantProps } from '../types';

function useCurrentUserId(): string | null {
  // Include the session marker in the key so the cache is invalidated automatically
  // when the user logs out (displayName → null) or a different user logs in.
  // The actual auth token is an HttpOnly cookie inaccessible to JS; displayName is
  // the only synchronous per-user identifier available in localStorage.
  const sessionMarker = authService.getDisplayName();
  const { data } = useQuery({
    queryKey: ['current-user-id', sessionMarker],
    queryFn: async () => {
      if (!authService.hasSession()) return null;
      try {
        const user = await authService.fetchCurrentUser();
        return user.id;
      } catch {
        return null;
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return data ?? null;
}

export function CreditsBarVariant(props: Readonly<VariantProps>) {
  const currency = props.priceOptions.currency ?? 'USD';
  const userId = useCurrentUserId();

  const freeShipping = useFreeShipping(props.total, currency);
  const credits = useCreditsProgress(props.total, userId, currency);

  return (
    <>
      <FreeShippingProgressBar
        freeShipping={freeShipping}
        priceOptions={props.priceOptions}
      />
      <CreditsProgressBar
        credits={credits}
        priceOptions={props.priceOptions}
      />
      <DefaultVariant {...props} />
    </>
  );
}
